import { Injectable, isDevMode } from '@angular/core';

const STORAGE_KEY = 'marina_last_input_at';
const CHANNEL_NAME = 'marina-auth';

/**
 * Ceiling for how long input counts as "recent" when deciding whether a request was user-driven.
 * Scaled down for short idle windows by configureFor().
 */
const MAX_RECENT_INPUT_WINDOW_MS = 60_000;

/** Never narrower than this, however short the idle window. */
const MIN_RECENT_INPUT_WINDOW_MS = 10_000;

/** Ceiling for the heartbeat. Scaled down for short idle windows — see configureFor(). */
const MAX_HEARTBEAT_INTERVAL_MS = 60_000;

/** Never faster than this, however short the idle window. */
const MIN_HEARTBEAT_INTERVAL_MS = 10_000;

/**
 * When the user last did something, shared across every tab of this origin.
 *
 * Two things depend on knowing this, and both used to get it wrong.
 *
 * The **idle timer** used to run per tab off that tab's own events, so a user working in one tab
 * while another sat open on a second monitor would be signed out by the idle one — it had seen no
 * mouse or key for an hour and had no way to know the other had. Reading a shared value fixes that,
 * and also fixes the tab that comes back from being suspended believing an hour of nothing happened.
 *
 * The **server** used to treat any request as activity, which meant a page that polls — every portal
 * page — renewed its own idle window forever, so the policy could only end sessions that were
 * already closed. The server now hears about a person being present, not traffic: a header on
 * requests the user caused, and a heartbeat while this tab is visible (customer dashboards make no
 * HTTP after load — without that beat they were signed out by the refresh timer after 20–30 minutes).
 */
@Injectable({ providedIn: 'root' })
export class UserActivityService {
  private lastInputAt = 0;
  private lastSharedWriteAt = 0;
  private channel: BroadcastChannel | null = null;
  private tracking = false;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastHeartbeatAt = 0;
  private heartbeatIntervalMs = MAX_HEARTBEAT_INTERVAL_MS;
  private recentInputWindowMs = MAX_RECENT_INPUT_WINDOW_MS;
  private heartbeatEnabled = true;

  /** Supplied by AuthService so this does not have to inject HttpClient (and the interceptor chain). */
  private sendHeartbeat: (() => Promise<void>) | null = null;

  constructor() {
    this.lastInputAt = this.readShared() ?? 0;
    this.listenForOtherTabs();
  }

  registerHeartbeatSender(send: () => Promise<void>): void {
    this.sendHeartbeat = send;
  }

  /**
   * Tune the heartbeat to the session's actual idle policy.
   *
   * A fixed one-minute heartbeat is fine against a one-hour window and useless against a short one:
   * the first beat would land exactly on the deadline, or after it. It is now a third of the window,
   * capped at a minute, so it always gets at least two chances to speak before the window closes.
   *
   * Remember Me sessions have no idle timeout at all, so there is nothing for a heartbeat to hold
   * open and it is switched off — otherwise it would be a request a minute, forever, for nothing.
   */
  configureFor(idleTimeoutMinutes: number | null | undefined, rememberMe: boolean): void {
    this.heartbeatEnabled = !rememberMe;

    const windowMs = (idleTimeoutMinutes ?? 0) * 60_000;
    this.heartbeatIntervalMs = windowMs > 0
      ? Math.max(MIN_HEARTBEAT_INTERVAL_MS, Math.min(MAX_HEARTBEAT_INTERVAL_MS, Math.floor(windowMs / 3)))
      : MAX_HEARTBEAT_INTERVAL_MS;

    // "Did the user cause this request?" has to be judged against the window it protects too. A
    // fixed minute against a one-minute idle window would mark every request for a full window after
    // a single click as user-driven, which is most of the way to counting traffic again.
    this.recentInputWindowMs = windowMs > 0
      ? Math.max(MIN_RECENT_INPUT_WINDOW_MS, Math.min(MAX_RECENT_INPUT_WINDOW_MS, Math.floor(windowMs / 3)))
      : MAX_RECENT_INPUT_WINDOW_MS;

    if (this.tracking) {
      this.restartHeartbeat();
    }
  }

  /**
   * The most recent input seen by **any** tab, in epoch milliseconds. 0 when nothing is known.
   */
  getLastInputAt(): number {
    // Always re-read: another tab's write may not have reached us as an event yet, and the caller is
    // usually about to decide whether to end the session.
    const shared = this.readShared();
    if (shared !== null && shared > this.lastInputAt) {
      this.lastInputAt = shared;
    }
    return this.lastInputAt;
  }

  /** Whether somebody interacted within `windowMs` — the question the interceptor asks. */
  hasRecentInput(windowMs: number = this.recentInputWindowMs): boolean {
    const last = this.getLastInputAt();
    return last > 0 && Date.now() - last <= windowMs;
  }

  /**
   * Start watching for input. Idempotent, and safe to call on every login.
   */
  start(): void {
    if (this.tracking || typeof document === 'undefined') {
      return;
    }
    this.tracking = true;

    // Real interaction only. Deliberately not scroll or mousemove-on-a-timer from a kiosk page:
    // the point is to distinguish a person from a page.
    const events = ['mousedown', 'keydown', 'touchstart', 'click', 'wheel'];
    for (const event of events) {
      document.addEventListener(event, this.onInput, { passive: true, capture: true });
    }

    // A tab that has been suspended wakes up believing nothing has happened. It has to ask.
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.restartHeartbeat();
  }

  private restartHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (!this.heartbeatEnabled) {
      return;
    }
    this.heartbeatTimer = setInterval(() => void this.maybeSendHeartbeat(), this.heartbeatIntervalMs);
  }

  /** Stop watching. Called when a session ends. */
  stop(): void {
    if (typeof document !== 'undefined') {
      const events = ['mousedown', 'keydown', 'touchstart', 'click', 'wheel'];
      for (const event of events) {
        document.removeEventListener(event, this.onInput, { capture: true } as EventListenerOptions);
      }
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.tracking = false;
  }

  /** Record input directly. Used by tests, and by anything that knows better than a DOM event. */
  noteInput(at: number = Date.now()): void {
    if (at <= this.lastInputAt) {
      return;
    }
    this.lastInputAt = at;
    this.lastSharedWriteAt = Date.now();
    this.writeShared(at);
    this.broadcast(at);
  }

  private readonly onInput = (): void => {
    const now = Date.now();
    if (now > this.lastInputAt) {
      this.lastInputAt = now;
    }
    // Writing on every keystroke would be pointless traffic to localStorage and to every other tab,
    // so writes are throttled to one a second. The throttle is measured from the previous *write*,
    // not the previous input: measured from the input it never elapses while someone types or scrolls
    // continuously, and the other tabs would be looking at a value as old as the whole burst.
    if (now - this.lastSharedWriteAt < 1000) {
      return;
    }
    this.lastSharedWriteAt = now;
    this.writeShared(now);
    this.broadcast(now);
  };

  private readonly onVisibilityChange = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      // Adopt whatever happened elsewhere while this tab was away, before anything acts on the value.
      this.getLastInputAt();
      // A tab that just came back should say so immediately if the interval has elapsed — waiting
      // for the next tick can land after the idle deadline.
      void this.maybeSendHeartbeat();
    }
  };

  /**
   * Tell the server this tab is still in front of someone.
   *
   * A visible tab is the customer-app equivalent of portal polling: that app's pages keep making
   * authenticated HTTP calls, and with the activity-header gate off those calls renew idle.
   * Customer dashboards are SignalR-only after load, so sitting still produced no HTTP at all
   * except the token-refresh timer — which the server deliberately does not count. The session
   * then died on the next refresh (`timer-refresh-rejected`) after 20–30 minutes.
   *
   * A hidden tab still idles out; we only speak while `visibilityState === 'visible'`.
   */
  private async maybeSendHeartbeat(): Promise<void> {
    if (!this.sendHeartbeat || !this.heartbeatEnabled) {
      return;
    }
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return;
    }
    if (Date.now() - this.lastHeartbeatAt < this.heartbeatIntervalMs) {
      return;
    }

    // One tab is enough: the session is shared, so the most recently visible tab speaks for all of
    // them. Whichever tab's timer fires first while it is in view does the talking.
    this.lastHeartbeatAt = Date.now();
    try {
      await this.sendHeartbeat();
    } catch (error) {
      // A missed heartbeat costs nothing on its own — the next one is a minute away, and ordinary
      // requests carry the same signal.
      this.log('[UserActivity] Heartbeat failed (ignored)', error);
    }
  }

  private readShared(): number | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private writeShared(at: number): void {
    try {
      localStorage.setItem(STORAGE_KEY, String(at));
    } catch {
      // Storage unavailable: this tab still tracks its own input, it just cannot share it.
    }
  }

  private broadcast(at: number): void {
    try {
      this.channel ??= typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
      this.channel?.postMessage({ type: 'user-activity', at });
    } catch {
      // The storage event is the fallback path.
    }
  }

  private listenForOtherTabs(): void {
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        this.channel ??= new BroadcastChannel(CHANNEL_NAME);
        this.channel.addEventListener('message', (event: MessageEvent) => {
          const data = event.data as { type?: string; at?: number } | null;
          if (data?.type === 'user-activity' && typeof data.at === 'number' && data.at > this.lastInputAt) {
            this.lastInputAt = data.at;
          }
        });
      }
    } catch {
      // ignore
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event: StorageEvent) => {
        if (event.key !== STORAGE_KEY || !event.newValue) return;
        const at = Number(event.newValue);
        if (Number.isFinite(at) && at > this.lastInputAt) {
          this.lastInputAt = at;
        }
      });
    }
  }

  private log(...args: unknown[]): void {
    if (isDevMode()) {
      console.log(...args);
    }
  }
}
